import { useState } from "react";
import { ChevronLeft, ChevronRight, Trash2, Globe, Trophy } from "lucide-react";

const DAY_MS = 86400000;
const fmtISO = (d) => d.toISOString().slice(0, 10);
const startOfWeek = (d) => { const x = new Date(d); x.setDate(x.getDate() - x.getDay()); return x; };

function onDay(ev, iso) {
  const start = ev.date;
  const end = ev.end_date || ev.date;
  return start <= iso && iso <= end;
}

function EventChip({ ev, onDelete, compact }) {
  return (
    <div data-testid={`schedule-event-${ev.id}`}
      className={`flex items-center gap-1.5 px-2 py-1 text-[11px] border ${ev.external ? "border-pb-cyan/40 bg-pb-cyan/5 text-pb-cyan" : "border-pb-red/40 bg-pb-red/5 text-pb-red"}`}>
      {ev.external ? <Globe className="w-3 h-3 shrink-0" /> : <Trophy className="w-3 h-3 shrink-0" />}
      <span className="truncate flex-1 font-semibold">{ev.name}</span>
      {!compact && ev.location && <span className="text-zinc-500 truncate hidden sm:inline">{ev.location}{ev.state ? `, ${ev.state}` : ""}</span>}
      {ev.external && onDelete && (
        <button data-testid={`schedule-delete-${ev.id}`} onClick={() => onDelete(ev.id)} className="text-zinc-600 hover:text-red-400">
          <Trash2 className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

export default function TournamentSchedule({ events, onDelete }) {
  const [view, setView] = useState("month");
  const [cursor, setCursor] = useState(new Date());

  const shift = (dir) => {
    const c = new Date(cursor);
    if (view === "day") c.setDate(c.getDate() + dir);
    else if (view === "week") c.setDate(c.getDate() + 7 * dir);
    else c.setMonth(c.getMonth() + dir);
    setCursor(c);
  };

  const title = view === "month"
    ? cursor.toLocaleDateString(undefined, { month: "long", year: "numeric" })
    : view === "week"
      ? `Week of ${startOfWeek(cursor).toLocaleDateString(undefined, { month: "short", day: "numeric" })}`
      : cursor.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });

  return (
    <div data-testid="tournament-schedule">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <button data-testid="schedule-prev" onClick={() => shift(-1)} className="w-8 h-8 border border-pb-border flex items-center justify-center hover:border-pb-red transition-colors"><ChevronLeft className="w-4 h-4" /></button>
          <button data-testid="schedule-next" onClick={() => shift(1)} className="w-8 h-8 border border-pb-border flex items-center justify-center hover:border-pb-red transition-colors"><ChevronRight className="w-4 h-4" /></button>
          <span className="font-display font-bold uppercase text-sm ml-1" data-testid="schedule-title">{title}</span>
        </div>
        <div className="flex border border-pb-border" data-testid="schedule-view-toggle">
          {["day", "week", "month"].map((v) => (
            <button key={v} data-testid={`schedule-view-${v}`} onClick={() => setView(v)}
              className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest transition-colors ${view === v ? "bg-pb-red text-black" : "text-zinc-500 hover:text-white"}`}>
              {v}
            </button>
          ))}
        </div>
      </div>

      {view === "month" && (
        <div>
          <div className="grid grid-cols-7 text-center text-[9px] uppercase tracking-widest text-zinc-600 mb-1">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <div key={d}>{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-px bg-pb-border/40">
            {(() => {
              const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
              const offset = first.getDay();
              const days = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
              const cells = [];
              for (let i = 0; i < offset; i++) cells.push(<div key={`e${i}`} className="bg-pb-base min-h-[64px]" />);
              for (let d = 1; d <= days; d++) {
                const iso = fmtISO(new Date(Date.UTC(cursor.getFullYear(), cursor.getMonth(), d)));
                const evs = events.filter((ev) => onDay(ev, iso));
                const today = iso === fmtISO(new Date());
                cells.push(
                  <div key={d} className={`bg-pb-surface min-h-[64px] p-1 ${today ? "outline outline-1 outline-pb-red" : ""}`}>
                    <div className={`text-[10px] font-mono ${today ? "text-pb-red font-bold" : "text-zinc-600"}`}>{d}</div>
                    <div className="space-y-0.5">
                      {evs.slice(0, 2).map((ev) => <EventChip key={ev.id} ev={ev} compact />)}
                      {evs.length > 2 && <div className="text-[9px] text-zinc-500">+{evs.length - 2} more</div>}
                    </div>
                  </div>
                );
              }
              return cells;
            })()}
          </div>
        </div>
      )}

      {view === "week" && (
        <div className="divide-y divide-pb-border/50 border border-pb-border">
          {Array.from({ length: 7 }).map((_, i) => {
            const day = new Date(startOfWeek(cursor).getTime() + i * DAY_MS);
            const iso = fmtISO(day);
            const evs = events.filter((ev) => onDay(ev, iso));
            return (
              <div key={i} className="flex gap-3 px-3 py-2 items-start">
                <div className={`w-20 shrink-0 text-xs ${iso === fmtISO(new Date()) ? "text-pb-red font-bold" : "text-zinc-500"}`}>
                  {day.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}
                </div>
                <div className="flex-1 space-y-1">
                  {evs.length === 0 ? <span className="text-xs text-zinc-700">—</span>
                    : evs.map((ev) => <EventChip key={ev.id} ev={ev} onDelete={onDelete} />)}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {view === "day" && (
        <div className="border border-pb-border p-4 space-y-2 min-h-[100px]">
          {events.filter((ev) => onDay(ev, fmtISO(cursor))).length === 0 && (
            <div className="text-sm text-zinc-600">Nothing scheduled this day.</div>
          )}
          {events.filter((ev) => onDay(ev, fmtISO(cursor))).map((ev) => (
            <div key={ev.id} className="space-y-1">
              <EventChip ev={ev} onDelete={onDelete} />
              {ev.notes && <div className="text-xs text-zinc-500 pl-2">{ev.notes}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
